/**
 * The read-only projection published on `window.__gameState` (BRIEF <architecture>).
 *
 * This is the verification surface: every Playwright assertion reads it rather than scraping the
 * DOM. It is rebuilt and frozen on each render, so page-side mutation cannot affect the game.
 */

/**
 * `setup` is reached exactly once, on the very first launch, to capture the pre-game baseline
 * (T46b, SPEC §8.7). It is a phase rather than a modal over `entry` because it is a step in the
 * boot sequence, and the machine is the only thing allowed to know about sequence.
 */
export type Phase =
  | 'boot'
  | 'setup'
  | 'entry'
  /** The one-off conversion demonstration, before the first run. Scores nothing (D-252). */
  | 'demo'
  | 'run'
  | 'reveal'
  | 'tracker'
  | 'log'
  | 'vetting';

export type RunPhase =
  | 'idle'
  | 'drawing'
  /** First exposure: the item is being shown before it is ever asked for (T59c). Unscored. */
  | 'teaching'
  | 'presenting'
  | 'awaiting'
  | 'grading'
  | 'feedback'
  | 'encoding';

export type Tier = 1 | 2 | 3 | 4 | 5 | 6;

export type ModeId =
  | 'tiles'
  | 'cloze'
  | 'conjugate'
  | 'conversion'
  | 'sentence'
  | 'speech'
  | 'encoding';

/**
 * Telemetry definitions are FROZEN from first launch so weeks 1-6 stay comparable
 * (RESEARCH.md §4 — the novelty effect is a live confound). Changing a definition requires a
 * `schemaVersion` bump and a DECISIONS.md entry.
 */
export interface Telemetry {
  schemaVersion: number;
  firstLaunchAt: number | null;
  weekNumber: number;
  /** 28-day, first-attempt unhinted production. Null until there is anything to measure. */
  retentionPct: number | null;
  /**
   * Never a ratio: recognition is structurally 0, so a ratio would be a division by zero dressed
   * up as a measurement. Two counts instead, so the claim "nothing here is a recognition task"
   * is directly auditable in the export.
   */
  productionVsRecognition: { production: number; recognition: number };
  /** Deliberately two fields. These are NEVER summed into one figure (SPEC §8.4). */
  minutesPlayed: { appMeasured: number; manualLogged: number };
  perTierAccuracy: Record<'1' | '2' | '3' | '4' | '5' | '6', number | null>;
  /** 7-day rolling Conversión + Motor share, against the 25% cap. */
  grammarSharePct: number | null;
  queueDepth: number;
  estimatedQueueMinutes: number;
  intakeSuppressed: boolean;
  hintedAttemptPct: number | null;
  speechAvailable: boolean;
  standalone: boolean;
}

export interface RunSummary {
  runId: string;
  startedAt: number;
  endedAt: number;
  itemsAttempted: number;
  itemsBanked: number;
  promoted: string[];
  returningTomorrow: string[];
  rulesUnlocked: string[];
  newItemsIntroduced: string[];
}

export interface PublicGameState {
  phase: Phase;
  runPhase: RunPhase;
  tier: Tier | null;
  activeItemId: string | null;
  mode: ModeId | null;
  roundTimerMs: number;
  queueDepth: number;
  runSummary: RunSummary | null;
  telemetry: Telemetry;
  ready: boolean;
  buildId: string;
  seed: number;
}

export const TELEMETRY_SCHEMA_VERSION = 1;

export function emptyTelemetry(): Telemetry {
  return {
    schemaVersion: TELEMETRY_SCHEMA_VERSION,
    firstLaunchAt: null,
    weekNumber: 1,
    retentionPct: null,
    productionVsRecognition: { production: 0, recognition: 0 },
    minutesPlayed: { appMeasured: 0, manualLogged: 0 },
    perTierAccuracy: { '1': null, '2': null, '3': null, '4': null, '5': null, '6': null },
    grammarSharePct: null,
    queueDepth: 0,
    estimatedQueueMinutes: 0,
    intakeSuppressed: false,
    hintedAttemptPct: null,
    speechAvailable: false,
    standalone: false,
  };
}

declare global {
  interface Window {
    __gameState?: Readonly<PublicGameState>;
  }
}

function deepFreeze<T>(value: T): T {
  if (value !== null && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.freeze(value);
    for (const key of Object.keys(value as object)) {
      deepFreeze((value as Record<string, unknown>)[key]);
    }
  }
  return value;
}

/**
 * Holds the mutable projection and publishes a frozen structural copy.
 *
 * Kept deliberately dumb: it is a mirror of game state, never a source of truth. Nothing may read
 * gameplay decisions back out of it.
 */
export class GameStateStore {
  private state: PublicGameState;

  constructor(initial: PublicGameState) {
    this.state = initial;
  }

  get(): PublicGameState {
    return this.state;
  }

  patch(partial: Partial<PublicGameState>): void {
    this.state = { ...this.state, ...partial };
  }

  patchTelemetry(partial: Partial<Telemetry>): void {
    this.state = { ...this.state, telemetry: { ...this.state.telemetry, ...partial } };
  }

  /** Publish a frozen snapshot to `window.__gameState`. Called once per render. */
  publish(target: Window = window): void {
    target.__gameState = deepFreeze(structuredClone(this.state)) as Readonly<PublicGameState>;
  }
}

export function initialGameState(buildId: string, seed: number): PublicGameState {
  return {
    phase: 'boot',
    runPhase: 'idle',
    tier: null,
    activeItemId: null,
    mode: null,
    roundTimerMs: 0,
    queueDepth: 0,
    runSummary: null,
    telemetry: emptyTelemetry(),
    ready: false,
    buildId,
    seed,
  };
}
